import React, { useState, useEffect } from "react";
import Table from "rc-table";
import "../../styling/Dashboard.css";
import { PiCaretUpDownFill } from "react-icons/pi";
import axios from "axios";

const ClientsTable = ({ handleClientClick }) => {
  const [sortOrder, setSortOrder] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState([]);
  const [sortedData, setSortedData] = useState([]);

  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // 10 entries per page

  const onClientClick = (record) => {
    handleClientClick(record.key); // send clientId to parent
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/business/clients`,
          {
            withCredentials: true,
          }
        );

        const formattedData = res.data.data.map((client, index) => ({
          name: client.name,
          email: client.email,
          status: "Event Complete",
          key: client._id || index.toString(),
        }));

        setData(formattedData);
        setSortedData(formattedData); 
      } catch (err) {
        console.error("Error fetching clients:", err);
      }
    };

    fetchClients();
  }, []);

  // --- PAGINATION MATH ---
  // Calculate total pages based on the current sorted/filtered data
  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Slice the data to only show the current page's 10 items
  const paginatedData = sortedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Handle sorting when user clicks on column header
  const handleSort = (column) => {
    const order = sortOrder[column] === "asc" ? "desc" : "asc";

    setSortOrder({ [column]: order });

    const sorted = [...sortedData].sort((a, b) => {
      let valA = a[column];
      let valB = b[column];

      // Convert strings to lowercase for comparison
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return order === "asc" ? -1 : 1;
      if (valA > valB) return order === "asc" ? 1 : -1;
      return 0;
    });

    setSortedData(sorted); 
  };

  const handleDelete = async () => {
    try {
      for (const clientId of selectedRows) {
        await axios.delete(`${process.env.REACT_APP_API_URL}/business/deleteClient/${clientId}`, {
          withCredentials: true,
        });
      }

      // Remove deleted clients from state
      const updatedData = data.filter((item) => !selectedRows.includes(item.key));
      setData(updatedData);
      setSortedData(updatedData);
      setSelectedRows([]); // Clear checkboxes

      // Safety check: If we delete the last item on a page, jump back one page
      const newTotalPages = Math.ceil(updatedData.length / pageSize);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      } else if (newTotalPages === 0) {
        setCurrentPage(1);
      }

    } catch (err) {
      console.error("Error deleting clients:", err);
      alert("Failed to delete one or more clients.");
    }
  };

  // Define table columns
  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      render: (_, record) => (
        <input
          type="checkbox"
          onChange={(e) => {
            const isChecked = e.target.checked;
            setSelectedRows((prev) =>
              isChecked
                ? [...prev, record.key]
                : prev.filter((key) => key !== record.key)
            );
          }}
          checked={selectedRows.includes(record.key)}
        />
      ),
      width: 30,
      align: "center",
    },
    {
      title: (
        <div className="custom-header" onClick={() => handleSort("name")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Name
        </div>
      ),
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a
          href="#!"
          onClick={(e) => {
            e.preventDefault();
            onClientClick(record);
          }}
          style={{ color: "#1C1154", textDecoration: "none" }}
        >
          <h4 style={{ margin: 0 }}>{text}</h4>
        </a>
      ),
      align: "left",
      width: 150,
    },
    {
      title: (
        <div className="custom-header" onClick={() => handleSort("email")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Email
        </div>
      ),
      dataIndex: "email",
      key: "email",
      align: "left",
      width: 200,
    },
    {
      title: (
        <div className="custom-header" onClick={() => handleSort("status")}>
          <PiCaretUpDownFill className="table-header-icon" />
          Status
        </div>
      ),
      dataIndex: "status",
      key: "status",
      align: "left",
      width: 150,
    },
  ];

  // --- UPDATED CUSTOM FOOTER ---
  const customFooter = () => (
    <div
      style={{
        paddingBottom: "10px",
        marginLeft: "14px",
        marginRight: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between", // Places Delete on the left, Pagination on the right
      }}
    >
      {/* Left Side: Delete Button */}
      {/* <div style={{ display: "flex", alignItems: "baseline", width: "55%" }}>
        <button className="tableDownloadButton" onClick={handleDelete}>
          Delete
        </button>
      </div> */}

      {/* Right Side: Pagination Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            border: "none",
            background: "white",
            padding: "2px 8px",
            color: "#1c1156",
            fontSize: "25px",
            textDecoration: "none",
            transition: "none",
          }}
        >
          {"<"}
        </button>
        <button
          style={{
            background: "#fff",
            color: "#4D4D4D",
            border: "1px solid #D9D9D9",
            fontSize: "20px",
            width: "35px",
            textDecoration: "none",
          }}
        >
          {currentPage}
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          style={{
            cursor: (currentPage === totalPages || totalPages === 0) ? "not-allowed" : "pointer",
            border: "none",
            background: "white",
            borderRadius: "0px",
            padding: "2px 8px",
            color: "#1c1156",
            fontSize: "25px",
          }}
        >
          {">"}
        </button>
      </div>
    </div>
  );

  const rowClassName = () => "custom-table-row";

  const customRowClassName = () => "table-row-with-border";

  return (
    <div className="events-table">
      <Table
        columns={columns}
        data={paginatedData} // <-- Passed the sliced data here!
        scroll={{ x: "100%" }}
        rowClassName={(record, index) =>
          `${rowClassName(record, index)} ${customRowClassName(record, index)}`
        }
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  borderBottom: "1px solid #D9D9D9",
                  height: "50px",
                  textAlign: "left",
                }}
              >
                {props.children}
              </th>
            ),
          },
        }}
        footer={customFooter} // <-- Attached the new footer here!
      />
    </div>
  );
};

export default ClientsTable;